import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Heart, HandHeart, Target, CheckCircle2, ShieldCheck, ArrowRight, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EMERALD = '#047857';
const NAVY = '#0E2D5C';
const SLATE = '#334155';

const VALUES = [
  { t: 'Compassion First', b: 'Every patient treated with dignity, empathy, respect.', icon: Heart },
  { t: 'Community-Focused Care', b: 'Trust, reliability, responsiveness with referrers.', icon: HandHeart },
  { t: 'Patient-Centred Decisions', b: 'Ask: "What is safest and best for this patient right now?"', icon: Target },
  { t: 'Closed-Loop Care', b: 'Nothing is complete until communicated, acknowledged, and followed up.', icon: CheckCircle2, critical: true },
  { t: 'Accountability Without Blame', b: 'Errors reviewed, learned from — system improvement, not blame.', icon: ShieldCheck },
];

export default function AcceptCultureCharterPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  if (!user) { navigate('/login'); return null; }

  const accept = async () => {
    setSaving(true); setErr('');
    try {
      await axios.post(`${API}/api/users/me/accept-culture-charter`, {}, { withCredentials: true });
      await checkAuth();
      navigate('/');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to record acceptance. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${EMERALD}08 0%, transparent 70%)` }}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 text-sm" style={{ color: NAVY }}>
          <Sparkles className="w-4 h-4" style={{ color: EMERALD }} />
          <span className="font-semibold">SP Radiology Assistant</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">One last step before you come in</span>
        </div>
        <button onClick={logout} data-testid="charter-signout"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="accept-charter-page">
        <div className="text-center mb-8">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: EMERALD }}>SP Culture Charter · Acceptance required</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ color: NAVY }}>
            Welcome, {user.name?.split(' ')[0] || 'team'}.
          </h1>
          <p className="text-sm max-w-xl mx-auto" style={{ color: SLATE }}>
            Before you start using the app, please read and accept our Work Culture &amp; Clinical Philosophy. This is how we behave at SP Radiology — with every patient, every colleague, every referrer. Every time.
          </p>
        </div>

        <div className="rounded-2xl border-2 p-6 mb-6" style={{ borderColor: EMERALD, backgroundColor: `${EMERALD}08` }}>
          <blockquote className="pl-4 border-l-4 italic text-base" style={{ borderColor: EMERALD, color: SLATE }}>
            The patient is at the centre of every decision. No clinical interaction is complete until the loop is closed.
          </blockquote>
          <p className="text-[11px] text-muted-foreground mt-3">— SP Radiology Culture Charter · Version 1.0</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {VALUES.map((v, i) => (
            <div key={i} className={`rounded-xl border p-4 ${v.critical ? 'border-2' : ''}`}
              style={{ borderColor: v.critical ? EMERALD : `${EMERALD}30`, backgroundColor: v.critical ? `${EMERALD}06` : 'transparent' }}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: v.critical ? EMERALD : `${EMERALD}15`, color: v.critical ? 'white' : EMERALD }}>
                  <v.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-['Outfit'] font-bold text-sm" style={{ color: NAVY }}>{v.t}</h3>
                    {v.critical && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: EMERALD }}>CRITICAL</span>}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: SLATE }}>{v.b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 mb-4 text-xs" style={{ color: SLATE }}>
          <p className="font-semibold mb-1" style={{ color: NAVY }}>By accepting you agree to:</p>
          <ul className="space-y-0.5 list-disc pl-4">
            <li>Treat every patient, colleague and referrer with dignity, empathy and respect.</li>
            <li>Close the loop on every imaging result, procedure, biopsy and urgent finding.</li>
            <li>Communicate clearly, document completely, and escalate safety concerns without hesitation.</li>
            <li>Uphold zero tolerance for abuse, discrimination or harassment.</li>
          </ul>
          <p className="mt-2 text-[11px]">The full Charter is available in-app at <a href="/sops/work-culture" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: EMERALD }}>/sops/work-culture</a>. Your acceptance is timestamped and recorded in the governance log.</p>
        </div>

        {err && <div className="mb-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-600" data-testid="charter-error">{err}</div>}

        <label className="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer bg-card hover:bg-accent/30" style={{ borderColor: confirmed ? EMERALD : 'hsl(var(--border))' }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid="charter-checkbox"
            className="mt-0.5 w-5 h-5 cursor-pointer" style={{ accentColor: EMERALD }} />
          <span className="text-sm font-semibold" style={{ color: NAVY }}>
            I have read the Culture Charter and commit to upholding these values in my work at SP Radiology.
          </span>
        </label>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          <p className="text-[11px] text-muted-foreground max-w-xs">Can&apos;t accept? Sign out and speak to the Practice Manager.</p>
          <button onClick={accept} disabled={!confirmed || saving} data-testid="charter-accept-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: EMERALD }}>
            {saving ? 'Recording…' : 'I commit — continue'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
